/**
 * Money, in minor units. Every amount that crosses the proxy is a whole
 * number of the currency's smallest unit -- 1234 is GHS 12.34 -- because a
 * float cannot hold a tenth of a pesewa and a string cannot be added up.
 * Nothing in the app ever does arithmetic on a formatted string.
 *
 * Formatting is done here rather than through `Intl.NumberFormat` so it is
 * identical on every device and in tests, whatever locale data the runtime
 * happens to ship.
 */

export interface Currency {
  code: string;
  name: string;
  /** Decimal places: GHS 1.00 is 100 minor units. */
  minorUnit: number;
}

/**
 * Mirrors the `currencies` table seeded by the wallet migration. The server
 * is the authority -- a wallet's currency comes back on its summary -- and
 * this is only what the app needs to print one and to offer a choice before
 * a wallet exists.
 */
export const CURRENCIES: readonly Currency[] = [
  { code: 'GHS', name: 'Ghana Cedi', minorUnit: 2 },
  { code: 'USD', name: 'US Dollar', minorUnit: 2 },
  // The new markets' currencies (Liberia, Sierra Leone, The Gambia, São Tomé
  // and Príncipe), all of two decimals like the cedi.
  { code: 'LRD', name: 'Liberian Dollar', minorUnit: 2 },
  { code: 'SLE', name: 'Sierra Leonean Leone', minorUnit: 2 },
  { code: 'GMD', name: 'Gambian Dalasi', minorUnit: 2 },
  { code: 'STN', name: 'São Tomé and Príncipe Dobra', minorUnit: 2 },
];

const DEFAULT_MINOR_UNIT = 2;

/**
 * How a number is written in the language the customer chose: the
 * thousands and decimal separators. English keeps `1,234.50`. The app sets
 * this when the language changes (the staff portal never does, so it stays
 * English). The separators come from `Intl` once, here, so formatting
 * stays a plain string operation and identical for every amount.
 */
let separators = { group: ',', decimal: '.' };

/**
 * Separators for the languages Sao writes, held here because Hermes on a phone
 * does not always carry the locale data `Intl.NumberFormat` would answer from.
 * A narrow no-break space is what French groups with; a plain no-break space
 * is used instead because every font Sao ships draws it.
 */
const SEPARATORS: Record<string, { group: string; decimal: string }> = {
  fr: { group: '\u00a0', decimal: ',' },
  // Portuguese (São Tomé and Príncipe) groups with a space and writes a comma.
  pt: { group: '\u00a0', decimal: ',' },
};

export function setMoneyLocale(language: string): void {
  const code = language.split('-')[0] ?? language;
  separators = SEPARATORS[code] ?? { group: ',', decimal: '.' };
}

export function findCurrency(code: string): Currency | undefined {
  return CURRENCIES.find((currency) => currency.code === code);
}

/** The printed name, falling back to the code for a currency added server-side. */
export function currencyName(code: string): string {
  return findCurrency(code)?.name ?? code;
}

/** How many decimal places this currency has; 2 for anything unrecognised. */
export function minorUnitOf(code: string): number {
  return findCurrency(code)?.minorUnit ?? DEFAULT_MINOR_UNIT;
}

/** Minor units as a major-unit number. Only for a display that counts up. */
export function toMajor(minor: number, code: string): number {
  return minor / 10 ** minorUnitOf(code);
}

/**
 * `1234567` -> `1,234,567`. Grouping only; no sign, no decimals.
 *
 * Done by slicing rather than with the usual lookahead regex, whose
 * `(\d{3})+` backtracks super-linearly on a long run of digits.
 */
export function groupThousands(whole: string): string {
  const parts: string[] = [];
  for (let end = whole.length; end > 0; end -= 3) {
    parts.unshift(whole.slice(Math.max(0, end - 3), end));
  }
  return parts.join(separators.group);
}

/** A plain number with `places` decimals in the chosen language: `2.4`, `2,4`. */
export function formatDecimal(value: number, places: number): string {
  const [whole = '', fraction] = value.toFixed(places).split('.');
  return fraction ? `${whole}${separators.decimal}${fraction}` : whole;
}

export interface SplitAmount {
  /** Includes the minus sign and the thousands separators. */
  whole: string;
  /** The decimal point and the minor digits, or '' for a zero-decimal currency. */
  fraction: string;
}

/**
 * The two halves the card face prints at different weights: whole units at
 * full ink, minor units dimmed. Split here rather than in the component so
 * the rounding and the sign are decided in one place.
 */
export function splitMinor(minor: number, code: string): SplitAmount {
  const places = minorUnitOf(code);
  const negative = minor < 0;
  const digits = Math.abs(Math.trunc(minor))
    .toString()
    .padStart(places + 1, '0');
  const whole = places === 0 ? digits : digits.slice(0, -places);
  const fraction =
    places === 0 ? '' : `${separators.decimal}${digits.slice(-places)}`;
  return { whole: `${negative ? '-' : ''}${groupThousands(whole)}`, fraction };
}

/** `1234` GHS -> `1,234.00` -- the number alone, for a field or a row. */
export function formatMinor(minor: number, code: string): string {
  const { whole, fraction } = splitMinor(minor, code);
  return `${whole}${fraction}`;
}

/** `1234` GHS -> `GHS 1,234.00` -- what a balance is labelled with. */
export function formatMoney(minor: number, code: string): string {
  return `${code} ${formatMinor(minor, code)}`;
}

/**
 * A signed amount for a statement row: `+ GHS 25.00` in, `- GHS 25.00` out.
 * The direction comes from the ledger, not from the sign of the amount --
 * an amount on a transaction is always positive, and which side of it the
 * wallet is on is what makes it a credit or a debit.
 */
export function formatSigned(
  minor: number,
  code: string,
  direction: 'in' | 'out',
): string {
  return `${direction === 'in' ? '+' : '-'} ${formatMoney(Math.abs(minor), code)}`;
}

/**
 * What someone typed, with the thousands separators gone and the decimal
 * mark as a point. In a language that writes the decimal as a comma (French, Portuguese)
 * a comma followed by one or two digits at the end is the decimal mark, so
 * "12,50" is 12.50 and not 1250; English keeps reading a comma as grouping.
 */
function normalizeTypedAmount(input: string): string {
  const spaced = input.trim().replace(/[\s\u00a0\u202f]/g, '');
  if (separators.decimal === ',') {
    const decimal = /^(.*),(\d{1,2})$/.exec(spaced);
    if (decimal)
      return `${(decimal[1] ?? '').replaceAll(/[.,]/g, '')}.${decimal[2]}`;
  }
  return spaced.replaceAll(',', '');
}

/**
 * What someone typed, as minor units. Accepts thousands separators and a
 * single decimal point; refuses more minor digits than the currency has
 * rather than rounding them away, because silently turning 10.005 into
 * 10.00 is the kind of help nobody asked for.
 *
 * Returns null for anything it cannot read exactly.
 */
export function parseAmount(input: string, code: string): number | null {
  const text = normalizeTypedAmount(input);
  if (!text) return null;

  const match = /^(\d*)(?:\.(\d*))?$/.exec(text);
  if (!match) return null;

  const [, wholeText = '', fractionText = ''] = match;
  if (!wholeText && !fractionText) return null;

  const places = minorUnitOf(code);
  if (fractionText.length > places) return null;

  const minor =
    Number(wholeText || '0') * 10 ** places +
    Number(fractionText.padEnd(places, '0') || '0');

  return Number.isSafeInteger(minor) ? minor : null;
}

/** `1009000203841` -> `1009 0002 0384 1`: an account number, in fours. */
export function groupDigits(value: string, size = 4): string {
  const digits = value.replace(/\s/g, '');
  const group = new RegExp(`.{1,${size}}`, 'g');
  const groups: string[] = [];
  for (let match = group.exec(digits); match; match = group.exec(digits)) {
    groups.push(match[0]);
  }
  return groups.join(' ');
}

/**
 * A wallet number as SAO prints it: `7712345678901` -> `771 234 567 8901`.
 * Anything else (an old 16-digit number) falls back to groups of four.
 */
export function formatWalletNumber(value: string): string {
  const digits = value.replace(/\s/g, '');
  const parts = /^(\d{3})(\d{3})(\d{3})(\d{4})$/.exec(digits);
  return parts ? parts.slice(1).join(' ') : groupDigits(digits);
}

/** The digits a wallet or card is known by in conversation. */
export function lastFour(value: string): string {
  return value.replace(/\D/g, '').slice(-4);
}

/**
 * A SAO wallet number: `77`, ten digits and a Luhn check digit, spaces
 * ignored. The same rule the server applies, so a typo is caught before a
 * lookup is spent on it.
 */
export function isValidWalletNumber(value: string): boolean {
  const digits = value.replace(/\s/g, '');
  return /^77\d{11}$/.test(digits) && passesLuhn(digits);
}

/**
 * A wallet number from before 2026-09-24, when they had 16 digits. For 90
 * days the server still finds the wallet by it and says its number now.
 */
export function isOldWalletNumber(value: string): boolean {
  const digits = value.replace(/\s/g, '');
  return /^77\d{14}$/.test(digits) && passesLuhn(digits);
}

function passesLuhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = Number(digits[i]);
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return sum % 10 === 0;
}
